// src/services/signalRManager.js
// Global SignalR connection manager for logout and other operations
import * as signalR from "@microsoft/signalr";
import chatService from "../API/chatService";

import { SIGNALR_HUB_URL } from "../config/api";

const HUB_URL = SIGNALR_HUB_URL;

class SignalRManager {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.onlineUsersCache = [];
    this.eventHandlers = {
      onMessage: [],
      onGroupMessage: [],
      onUserJoined: [],
      onUserLeft: [],
      onOnlineUsersUpdated: [],
      onTypingUsersUpdated: [],
    };
  }

  // Get the current connection
  getConnection() {
    return this.connection;
  }

  // Check if connected
  getIsConnected() {
    return this.isConnected;
  }

  // Create and start connection
  async startConnection() {
    if (this.connection && this.isConnected) {
      return this.connection;
    }

    const token = chatService.getToken();
    if (!token) {
      console.error("No token found for SignalR connection");
      return null;
    }

    // Create connection with token in query string
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${HUB_URL}?access_token=${token}`, {
        skipNegotiation: false,
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect()
      .build();

    // Set up event handlers
    this.setupEventHandlers();

    try {
      await this.connection.start();
      this.isConnected = true;
      await this.syncOnlineUsers();
      console.log("SignalR Connected!");
      return this.connection;
    } catch (error) {
      console.error("Error starting SignalR connection:", error);
      this.isConnected = false;
      return null;
    }
  }

  // Setup event handlers
  setupEventHandlers() {
    if (!this.connection) return;

    // Wrap handlers to call registered listeners
    this.connection.on("ReceiveMessage", (message) => {
      this.eventHandlers.onMessage.forEach((handler) => handler(message));
    });
    this.connection.on("ReceiveGroupMessage", (message) => {
      this.eventHandlers.onGroupMessage.forEach((handler) => handler(message));
    });

    this.connection.on("UserJoined", (user) => {
      this.eventHandlers.onUserJoined.forEach((handler) => handler(user));
    });

    this.connection.on("UserLeft", (studentId) => {
      this.eventHandlers.onUserLeft.forEach((handler) => handler(studentId));
    });

    this.connection.on("OnlineUsersUpdated", (users) => {
      this.onlineUsersCache = Array.isArray(users) ? users : [];
      this.eventHandlers.onOnlineUsersUpdated.forEach((handler) =>
        handler(this.onlineUsersCache)
      );
    });

    this.connection.on("TypingUsersUpdated", (users) => {
      this.eventHandlers.onTypingUsersUpdated.forEach((handler) =>
        handler(users)
      );
    });

    this.connection.on("MessageDeleted", (messageId) => {
      // Handle message deleted event - can be used by components
      console.log("Message deleted:", messageId);
    });

    this.connection.on("MessageUpdated", (message) => {
      // Handle message updated event - can be used by components
      console.log("Message updated:", message);
    });

    this.connection.on("Error", (error) => {
      console.error("SignalR error:", error);
    });

    this.connection.onclose((error) => {
      this.isConnected = false;
      if (error) {
        console.error("SignalR connection closed with error:", error);
      }
    });

    this.connection.onreconnecting((error) => {
      this.isConnected = false;
      console.log("SignalR reconnecting:", error);
    });

    this.connection.onreconnected((connectionId) => {
      this.isConnected = true;
      console.log("SignalR reconnected:", connectionId);
      this.syncOnlineUsers();
    });
  }

  // Register event handlers
  on(event, handler) {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event].push(handler);
      if (event === "onOnlineUsersUpdated" && this.onlineUsersCache.length > 0) {
        handler(this.onlineUsersCache);
      }
    }
  }

  // Remove event handlers
  off(event, handler) {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event] = this.eventHandlers[event].filter(
        (h) => h !== handler
      );
    }
  }

  // Disconnect and stop connection
  async disconnect() {
    if (this.connection) {
      try {
        // Stop automatic reconnection first
        if (this.connection.stop) {
          await this.connection.stop();
          console.log("SignalR connection stopped");
        }
        this.isConnected = false;
        this.connection = null;
        this.onlineUsersCache = [];
        // Clear all event handlers
        Object.keys(this.eventHandlers).forEach((key) => {
          this.eventHandlers[key] = [];
        });
        return true;
      } catch (error) {
        console.error("Error stopping SignalR connection:", error);
        this.connection = null;
        this.isConnected = false;
        return false;
      }
    }
    return true;
  }

  // Send message
  async sendMessage(message, imageUrl = null, audioUrl = null) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("SendMessage", message, imageUrl, audioUrl);
        return true;
      } catch (error) {
        console.error("Error sending message:", error);
        return false;
      }
    }
    return false;
  }

  async syncOnlineUsers() {
    try {
      const users = await chatService.getOnlineUsers();
      this.onlineUsersCache = Array.isArray(users) ? users : [];
      this.eventHandlers.onOnlineUsersUpdated.forEach((handler) =>
        handler(this.onlineUsersCache)
      );
    } catch (error) {
      console.error("Error syncing online users:", error);
    }
  }

  // Group chat: join/leave/send
  async joinGroup(groupId) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("JoinGroup", groupId);
        return true;
      } catch (error) {
        console.error("Error joining group:", error);
        return false;
      }
    }
    return false;
  }

  async leaveGroup(groupId) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("LeaveGroup", groupId);
        return true;
      } catch (error) {
        console.error("Error leaving group:", error);
        return false;
      }
    }
    return false;
  }

  async sendGroupMessage(groupId, message = null, imageUrl = null, audioUrl = null) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("SendGroupMessage", groupId, message, imageUrl, audioUrl);
        return true;
      } catch (error) {
        console.error("Error sending group message:", error);
        return false;
      }
    }
    return false;
  }

  // Start typing
  async startTyping() {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("StartTyping");
        return true;
      } catch (error) {
        console.error("Error starting typing:", error);
        return false;
      }
    }
    return false;
  }

  // Stop typing
  async stopTyping() {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("StopTyping");
        return true;
      } catch (error) {
        console.error("Error stopping typing:", error);
        return false;
      }
    }
    return false;
  }

  // Delete message via SignalR
  async deleteMessage(messageId) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("DeleteMessage", messageId);
        return true;
      } catch (error) {
        console.error("Error deleting message:", error);
        return false;
      }
    }
    return false;
  }

  // Update message via SignalR
  async updateMessage(messageId, newMessage) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("UpdateMessage", messageId, newMessage);
        return true;
      } catch (error) {
        console.error("Error updating message:", error);
        return false;
      }
    }
    return false;
  }

  // Friend Request Methods
  async sendFriendRequest(receiverId) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("SendFriendRequest", receiverId);
        return true;
      } catch (error) {
        console.error("Error sending friend request:", error);
        return false;
      }
    }
    return false;
  }

  async acceptFriendRequest(requestId) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("AcceptFriendRequest", requestId);
        return true;
      } catch (error) {
        console.error("Error accepting friend request:", error);
        return false;
      }
    }
    return false;
  }

  async rejectFriendRequest(requestId) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("RejectFriendRequest", requestId);
        return true;
      } catch (error) {
        console.error("Error rejecting friend request:", error);
        return false;
      }
    }
    return false;
  }

  async removeFriend(friendId) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("RemoveFriend", friendId);
        return true;
      } catch (error) {
        console.error("Error removing friend:", error);
        return false;
      }
    }
    return false;
  }

  // Private Chat Methods
  async sendPrivateMessage(toStudentId, message = null, imageUrl = null, videoUrl = null, audioUrl = null, replyToMessageId = null) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("SendPrivateMessage", toStudentId, message, imageUrl, videoUrl, audioUrl, replyToMessageId);
        return true;
      } catch (error) {
        console.error("Error sending private message:", error);
        return false;
      }
    }
    return false;
  }

  async startPrivateTyping(toStudentId) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("StartPrivateTyping", toStudentId);
        return true;
      } catch (error) {
        console.error("Error starting private typing:", error);
        return false;
      }
    }
    return false;
  }

  async stopPrivateTyping(toStudentId) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("StopPrivateTyping", toStudentId);
        return true;
      } catch (error) {
        console.error("Error stopping private typing:", error);
        return false;
      }
    }
    return false;
  }

  // Register event handlers for private chat
  onPrivateMessage(callback) {
    if (this.connection) {
      this.connection.on("ReceivePrivateMessage", callback);
    }
  }

  onPrivateTyping(callback) {
    if (this.connection) {
      this.connection.on("PrivateTyping", callback);
    }
  }

  offPrivateMessage(callback) {
    if (this.connection) {
      this.connection.off("ReceivePrivateMessage", callback);
    }
  }

  offPrivateTyping(callback) {
    if (this.connection) {
      this.connection.off("PrivateTyping", callback);
    }
  }

  async deletePrivateMessage(messageId) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("DeletePrivateMessage", messageId);
        return true;
      } catch (error) {
        console.error("Error deleting private message:", error);
        return false;
      }
    }
    return false;
  }

  async updatePrivateMessage(messageId, newMessage) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("UpdatePrivateMessage", messageId, newMessage);
        return true;
      } catch (error) {
        console.error("Error updating private message:", error);
        return false;
      }
    }
    return false;
  }
}

// Export singleton instance
const signalRManager = new SignalRManager();
export default signalRManager;

